import type { EventBus } from '../core/EventBus';
import type { GameEvents } from '../core/GameEvents';
import type { SegmentInstance } from '../level/segments/SegmentInstance';
import { withinTopReach } from '../level/terrain/terrainQueries';
import type { ObstacleCatalog } from './ObstacleCatalog';
import { ObstacleInstance } from './ObstacleInstance';

/**
 * Turns segment spawn plans into obstacle instances and keeps the active set.
 * Pure gameplay – ObstaclePresenter attaches the visuals.
 */
export class ObstacleManager {
  private readonly bySegment = new Map<number, ObstacleInstance[]>();
  private nextUid = 1;
  private readonly ceilingScratch: ObstacleInstance[] = [];
  private readonly topScratch: ObstacleInstance[] = [];

  constructor(
    private readonly catalog: ObstacleCatalog,
    private readonly events: EventBus<GameEvents>,
  ) {
    events.on('segment:spawned', ({ segment }) => this.spawnFor(segment));
    events.on('segment:recycled', ({ segment }) => this.despawnFor(segment));
  }

  /** Active obstacles on committed segments whose bounds overlap the track-distance range [minZ, maxZ]. */
  query(minZ: number, maxZ: number, out: ObstacleInstance[] = []): ObstacleInstance[] {
    out.length = 0;
    for (const list of this.bySegment.values()) {
      for (const obstacle of list) {
        if (!obstacle.active || !obstacle.segment.committed) continue;
        if (obstacle.bounds.maxZ > minZ && obstacle.bounds.minZ < maxZ) out.push(obstacle);
      }
    }
    return out;
  }

  /**
   * Lowest underside of overhead colliders (bottom above `minHeight`) inside the
   * given track-space footprint, or null if nothing hangs above it.
   */
  lowestCeiling(minZ: number, maxZ: number, minX: number, maxX: number, minHeight: number): number | null {
    let ceiling: number | null = null;
    for (const obstacle of this.query(minZ, maxZ, this.ceilingScratch)) {
      for (const c of obstacle.trackColliders) {
        if (c.minY < minHeight || c.maxZ <= minZ || c.minZ >= maxZ || c.maxX <= minX || c.minX >= maxX) continue;
        ceiling = ceiling === null ? c.minY : Math.min(ceiling, c.minY);
      }
    }
    return ceiling;
  }

  /**
   * Highest walkable obstacle top (ObstacleDefinition.walkableTop – container roofs) inside the
   * track-space footprint that lies at most at `topReach` (the runner's feet + one step), or null.
   * Higher tops are not ground for that runner: their colliders decide (front / side contact).
   */
  highestTopIn(minZ: number, maxZ: number, minX: number, maxX: number, topReach: number): number | null {
    let top: number | null = null;
    for (const obstacle of this.query(minZ, maxZ, this.topScratch)) {
      if (!obstacle.definition.walkableTop) continue;
      for (const c of obstacle.trackColliders) {
        if (!withinTopReach(c.maxY, topReach) || c.maxZ <= minZ || c.minZ >= maxZ || c.maxX <= minX || c.minX >= maxX) continue;
        top = top === null ? c.maxY : Math.max(top, c.maxY);
      }
    }
    return top;
  }

  /** All obstacles of one segment (including cleared ones). */
  forSegment(segmentUid: number): readonly ObstacleInstance[] {
    return this.bySegment.get(segmentUid) ?? [];
  }

  all(): ObstacleInstance[] {
    return [...this.bySegment.values()].flat();
  }

  /**
   * Takes an obstacle out of play but leaves its visual: a set piece takes it over (the parked dozer a runner
   * jumped onto drives off – dozer/DozerManager).
   */
  retire(obstacle: ObstacleInstance): void {
    obstacle.active = false;
  }

  /** Removes an obstacle from play (it was smashed by the player). */
  clearObstacle(obstacle: ObstacleInstance): void {
    if (!obstacle.active) return;
    obstacle.active = false;
    this.events.emit('obstacle:cleared', { obstacle });
  }

  private spawnFor(segment: SegmentInstance): void {
    const list: ObstacleInstance[] = [];
    for (const placement of segment.spawnPlan.obstacles) {
      const point = segment.definition.obstacleSpawnPoints.find((p) => p.id === placement.spawnPointId);
      if (!point) throw new Error(`${segment.definition.id}: unknown spawn point ${placement.spawnPointId}`);
      const obstacle = new ObstacleInstance(this.nextUid++, this.catalog.get(placement.obstacleId), segment, point, placement.yaw);
      list.push(obstacle);
      this.events.emit('obstacle:spawned', { obstacle });
    }
    this.bySegment.set(segment.uid, list);
  }

  private despawnFor(segment: SegmentInstance): void {
    const list = this.bySegment.get(segment.uid);
    if (!list) return;
    for (const obstacle of list) this.events.emit('obstacle:despawned', { obstacle });
    this.bySegment.delete(segment.uid);
  }
}
